/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import moment from 'moment';
import { MRT_ColumnDef } from 'material-react-table';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { Box, IconButton } from '@mui/material';
import { isEmpty } from 'lodash';

import { formatDateCell } from '../../Utilities/Jobs.Utils';
import { getMRTTableHeadStyles, hasEventId } from '../../../../Shared/Utilities/utils';
import { DateFormat, DayOfWeek, FiltersOfAllJobs, PROCESS_EVENTS, states } from '../../../../Shared/Constants/App';

import { IJobs } from '../../../Jobs/Models/JobsView.Model';
import { JobsAccessorKey } from './AllJobs.Model';
import { IProcessEvents } from '../../../../routes/config';

import { IJobOverview } from '../../../Jobs/Components/JobsListView/JobsListView';
import { TaskData } from '../../../Jobs/Components/WorkTasks/WorkTasks';
import { IBidList } from '../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../../../Customer/Components/ViewFacilityDetails/ViewFacilityDetails';
import { ColumnHeader } from '../AllSchedules/AllJobs.Style';
import { Icons } from '../../../../Shared/Constants/Icons';

export const getDayName = (dayIndex: number): string => {
  return DayOfWeek[dayIndex] ?? '-';
};

export const getAllJobsColumns = (
  renderCell: (value: React.ReactNode) => string | number,
  renderActionsCell: (row: any) => JSX.Element,
  t: (key: string) => string,
  filters: any[],
  handleFilterChange: (filterType: string, filterValue: string[], event?: React.MouseEvent<HTMLInputElement>) => void,
  isFiltersLoading: boolean,
  selectedJobNumber?: string[],
  selectedJobName?: string[],
  selectedJobAddress?: string[],
  selectedState?: string[],
  selectedCity?: string[],
  selectedVendor?: string[],
  selectedVendorNumber?: string[],
  selectedCustomer?: string[],
  selectedVendorAdminName?: string[],
  selectedVendorAdminEmail?: string[],
  selectedWorkOrderStatus?: string[],
  selectedCreatedOn?: string[],
  selectedWorkTicketNumber?: string[],
  handleClickVendor?: (data: any) => void,
  setIsFilterPopUpOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  setFilterName?: React.Dispatch<React.SetStateAction<string>>,
  jobManagementEvents?: IProcessEvents[]
): MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[] => [
  {
    accessorKey: JobsAccessorKey.JobNumber,
    header: t('vendor:jobNumber'),
    enableColumnFilter: true,
    enableSorting: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('vendor:jobNumber')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobNumber);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobNumber)),

    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.JobName,
    header: t('vendor:jobName'),
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('vendor:jobName')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobName);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobName)),

    size: 180,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
    enableColumnActions: false,
    sortingFn: 'alphanumericCaseSensitive',
  },
  {
    accessorKey: JobsAccessorKey.JobAddress,
    header: t('tableHeader:jobAddress'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('tableHeader:jobAddress')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.JobAddress);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedJobAddress)),
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.City,
    header: t('tableHeader:city'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.StateCode,
    header: t('tableHeader:state'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => {
      return states[renderedCellValue as keyof typeof states] || renderedCellValue;
    },
  },
  {
    accessorKey: JobsAccessorKey.JobType,
    header: t('tableHeader:jobType'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.WorkOrderStatus,
    header: t('dashboard:status'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.FulfillmentType,
    header: t('tableHeader:fulfillmentType'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ cell }) => {
      return (
        <>
          {!isEmpty(cell?.row?.original?.parentWorkOrderId)
            ? t('jobManagement:subContracted')
            : t('jobManagement:selfPerformed')}
        </>
      );
    },
  },
  {
    accessorKey: JobsAccessorKey.CustomerName,
    header: t('tableHeader:customerName'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: true,
    Header: () => (
      <ColumnHeader>
        <span>{t('tableHeader:customerName')}</span>
        <IconButton
          size="small"
          onClick={(e) => {
            e?.stopPropagation();
            setIsFilterPopUpOpen?.(true);
            setFilterName?.(FiltersOfAllJobs.Customer);
          }}>
          <FilterAltIcon fontSize="small" />
        </IconButton>
      </ColumnHeader>
    ),
    muiTableHeadCellProps: () => getMRTTableHeadStyles(!isEmpty(selectedCustomer)),
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.VendorNumber,
    header: t('tableHeader:vendorNumber'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.VendorName,
    header: t('tableHeader:vendorName'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ cell, renderedCellValue }) => {
      return (
        <>
          {jobManagementEvents && hasEventId(jobManagementEvents, PROCESS_EVENTS.ReassignVendor) ? (
            <Box
              onClick={() => handleClickVendor?.(cell)}
              sx={{
                textDecoration: 'underline',
                cursor: 'pointer',
              }}>
              {renderedCellValue ? (
                renderedCellValue
              ) : (
                <div
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                  }}>
                  <img src={Icons.AddTagIcon} />
                </div>
              )}
            </Box>
          ) : (
            renderedCellValue
          )}
        </>
      );
    },
  },
  {
    accessorKey: JobsAccessorKey.VendorAdminName,
    header: t('tableHeader:vendorAdminName'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.VendorAdminEmail,
    header: t('tableHeader:vendorAdminEmail'),
    size: 170,
    enableColumnActions: false,
    sortingFn: 'alphanumericCaseSensitive',
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    accessorKey: JobsAccessorKey.CreatedOn,
    header: t('tableHeader:createdOn'),
    size: 100,
    filterFn: (row, _columnIds, filterValue) => {
      const formattedValue = moment(row.getValue('createdOn')).format(DateFormat.StandardDate);
      return formattedValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    Cell: ({ cell }) => formatDateCell(cell),
    enableColumnActions: false,
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
  },
  {
    accessorKey: JobsAccessorKey.WorkTicketNumber,
    header: t('tableHeader:wTNumber'),
    enableHiding: true,
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
  },
  {
    header: t('tableHeader:actions'),
    enableColumnActions: false,
    enablePinning: false,
    enableHiding: true,
    size: 90,
    Cell: ({ row }) => renderActionsCell(row),
  },
];
